import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Loader2, Mail, Phone, User, Trash2, AlertTriangle, UserX, RefreshCw, KeyRound, Pencil, Printer, Cake } from "lucide-react";
import { EditCustomerDialog } from "@/components/EditCustomerDialog";
import { WelcomeResultDialog } from "@/components/WelcomeResultDialog";
import { CustomerProfileFields, customerProfilePayload } from "@/components/CustomerProfileFields";
import { CustomersImportExport } from "@/components/CustomersImportExport";
import { BirthdaysPanel } from "@/components/BirthdaysPanel";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { passwordError, PASSWORD_HINT } from "@/lib/password";

const empty = { name: "", email: "", phone: "", profile: { person_type: "pf" } };

export default function OwnerCustomers() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const ioStart = params.get("io") === "1";
  useEffect(() => { if (ioStart) { params.delete("io"); setParams(params, { replace: true }); } }, []); // eslint-disable-line
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toRemove, setToRemove] = useState(null);
  const [removing, setRemoving] = useState("");
  const [toReactivate, setToReactivate] = useState(null);
  const [reactivatePassword, setReactivatePassword] = useState("");
  const [reactivating, setReactivating] = useState(false);
  const [toEdit, setToEdit] = useState(null);
  const [welcome, setWelcome] = useState(null);
  const [showBdays, setShowBdays] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/customers");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.profile?.source) { toast.error("Origem do cliente: escolha como o cliente chegou"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/customers", { name: form.name, email: form.email, phone: form.phone, ...customerProfilePayload(form.profile) });
      setList((p) => [data, ...p]);
      setWelcome(data);
      setForm(empty);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (mode) => {
    if (!toRemove) return;
    setRemoving(mode);
    try {
      const { data: _ } = await api.delete(`/customers/${toRemove.id}`, { params: { mode } });
      if (mode === "hard") {
        setList((p) => p.filter((c) => c.id !== toRemove.id));
        toast.success("Cliente e histórico removidos");
      } else {
        setList((p) => p.map((c) => (c.id === toRemove.id ? { ...c, is_deleted: true } : c)));
        toast.success("Acesso do cliente desativado. Histórico mantido.");
      }
      setToRemove(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving("");
    }
  };

  const reactivate = async (e) => {
    e.preventDefault();
    if (!toReactivate) return;
    if (passwordError(reactivatePassword)) {
      toast.error(passwordError(reactivatePassword));
      return;
    }
    setReactivating(true);
    try {
      const { data } = await api.post(`/customers/${toReactivate.id}/reactivate`, { password: reactivatePassword });
      setList((p) => p.map((c) => (c.id === data.id ? data : c)));
      toast.success(`${data.name} foi reativado(a). Nova senha enviada por e-mail.`);
      setToReactivate(null);
      setReactivatePassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase text-zinc-100">Clientes</h1>
          <p className="text-sm text-zinc-400 mt-1">Cadastre os proprietários dos veículos atendidos pela sua oficina.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="birthdays-toggle" variant="outline" onClick={() => setShowBdays((s) => !s)}
            className={`h-12 rounded-2xl border-zinc-800 font-bold ${showBdays ? "bg-amber-brand/15 text-amber-300 border-amber-brand/50" : "bg-zinc-900 text-zinc-200"}`}>
            <Cake className="h-4 w-4 mr-2" /> Aniversariantes do mês
          </Button>
          <CustomersImportExport startOpen={ioStart} onImported={load} disabled={user?.shop_id === "all"} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-customer-btn"
              disabled={user?.shop_id === "all"}
              className="w-full sm:w-auto h-12 px-6 rounded-2xl bg-gradient-to-b from-green-neon to-green-brandDark text-zinc-950 font-bold uppercase tracking-wide shadow-[0_8px_28px_-10px_rgba(74,222,128,0.55)] hover:brightness-110 disabled:opacity-50"
            >
              <Plus className="h-5 w-5 mr-2" /> {user?.shop_id === "all" ? "Escolha uma unidade" : "Novo Cliente"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display uppercase">Novo cliente</DialogTitle>
              <DialogDescription className="text-zinc-500">
                O cliente receberá por e-mail o link do app, o e-mail cadastrado e a senha definida aqui.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="new-customer-form">
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Nome completo</Label>
                <Input data-testid="customer-name-input" required value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800" placeholder="José da Silva" />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">E-mail</Label>
                <Input data-testid="customer-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800" placeholder="cliente@email.com" />
              </div>
              <div>
                <Label className="text-xs font-mono-caps text-zinc-400">Telefone</Label>
                <Input data-testid="customer-phone-input" value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className="mt-1 bg-zinc-900 border-zinc-800" placeholder="(11) 99999-9999" />
              </div>
              <details data-testid="customer-profile-toggle" className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 open:bg-zinc-900/60">
                <summary className="cursor-pointer text-xs font-mono-caps text-zinc-400 uppercase tracking-wider">Dados completos (CPF/CNPJ, endereço…)</summary>
                <div className="mt-3">
                  <CustomerProfileFields value={form.profile} onChange={(prof) => setForm((p) => ({ ...p, profile: prof }))} idPrefix="customer-new" requireSource />
                </div>
              </details>
              <Button data-testid="customer-submit-btn" type="submit" disabled={saving}
                className="w-full h-11 bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar cliente"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showBdays && !loading && <BirthdaysPanel list={list} />}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>
      ) : list.length === 0 ? (
        <div data-testid="customers-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center">
          <User className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhum cliente cadastrado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => {
            const inactive = !!c.is_deleted;
            return (
              <div
                key={c.id}
                data-testid={`customer-card-${c.id}`}
                className={`rounded-2xl border p-4 flex items-center gap-4 ${inactive ? "border-card2-border bg-card2/60 opacity-80" : "border-card2-border bg-card2"}`}
              >
                <div className={`h-12 w-12 rounded-2xl border flex items-center justify-center shrink-0 ${inactive ? "bg-zinc-900 border-card2-border" : "bg-green-brand/15 border-green-brand/40"}`}>
                  <User className={`h-6 w-6 ${inactive ? "text-zinc-500" : "text-green-neon"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-base font-bold text-zinc-100 truncate">{c.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{c.email}</div>
                      {c.phone && (
                        <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3" /> {c.phone}
                        </div>
                      )}
                      {inactive && (
                        <span data-testid={`customer-inactive-badge-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-amber-brand/10 border border-amber-brand/40 rounded-full px-2 py-0.5 mt-1.5">
                          <UserX className="h-3 w-3" /> Desativado
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {inactive && (
                        <button
                          data-testid={`customer-reactivate-${c.id}`}
                          onClick={() => { setToReactivate(c); setReactivatePassword(""); }}
                          className="h-8 w-8 rounded-full flex items-center justify-center text-green-neon hover:text-green-300 hover:bg-green-950/40 border border-transparent hover:border-green-900"
                          aria-label="Reativar cliente"
                          title="Reativar cliente"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        data-testid={`customer-edit-${c.id}`}
                        onClick={() => setToEdit(c)}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-300 hover:text-green-neon hover:bg-green-950/40 border border-transparent hover:border-green-900"
                        aria-label="Editar cliente"
                        title="Editar cliente"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <Link
                        data-testid={`customer-crm-${c.id}`}
                        to={`/owner/customers/${c.id}/crm`}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-300 hover:text-green-neon hover:bg-green-950/40 border border-transparent hover:border-green-900"
                        aria-label="Ficha CRM"
                        title="Abrir ficha CRM"
                      >
                        <User className="h-4 w-4" />
                      </Link>
                      <Link
                        data-testid={`customer-sheet-${c.id}`}
                        to={`/owner/customers/${c.id}/ficha`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-300 hover:text-amber-brand hover:bg-amber-brand/10 border border-transparent hover:border-amber-brand/40"
                        aria-label="Imprimir ficha"
                        title="Imprimir ficha"
                      >
                        <Printer className="h-4 w-4" />
                      </Link>
                      <button
                        data-testid={`customer-delete-${c.id}`}
                        onClick={() => setToRemove(c)}
                        className="h-8 w-8 rounded-full flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-950/40 border border-transparent hover:border-red-900"
                        aria-label="Remover cliente"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EditCustomerDialog
        customer={toEdit}
        onClose={() => setToEdit(null)}
        onSaved={(data, keepOpen) => { setList((p) => p.map((c) => (c.id === data.id ? data : c))); if (!keepOpen) setToEdit(null); }}
      />

      <Dialog open={!!toRemove} onOpenChange={(o) => !o && setToRemove(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-brand" /> Excluir cliente
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {toRemove ? `Como você quer remover ${toRemove.name}?` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <button
              data-testid="customer-delete-soft"
              type="button"
              disabled={!!removing}
              onClick={() => remove("soft")}
              className="w-full text-left rounded-xl border border-amber-brand/60 bg-amber-50 dark:bg-amber-brand/5 hover:bg-amber-100 dark:hover:bg-amber-brand/10 p-4 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-amber-900 dark:text-amber-200">
                <UserX className="h-4 w-4" /> Desativar acesso (manter histórico)
              </div>
              <div className="text-xs text-amber-900/90 dark:text-amber-100/80 mt-1">
                O cliente perde o login, mas as ordens e fotos antigas continuam disponíveis para a oficina.
                Você pode reativar depois com uma nova senha.
              </div>
              {removing === "soft" && <Loader2 className="h-4 w-4 animate-spin text-amber-brand mt-2" />}
            </button>
            <button
              data-testid="customer-delete-hard"
              type="button"
              disabled={!!removing}
              onClick={() => remove("hard")}
              className="w-full text-left rounded-xl border border-red-400 dark:border-red-900 bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50 p-4 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-2 font-bold text-red-800 dark:text-red-200">
                <Trash2 className="h-4 w-4" /> Excluir tudo (irreversível)
              </div>
              <div className="text-xs text-red-900/90 dark:text-red-100/80 mt-1">
                Remove o cliente e TODAS as ordens, orçamentos e fotos vinculadas. Ação não pode ser desfeita.
              </div>
              {removing === "hard" && <Loader2 className="h-4 w-4 animate-spin text-red-500 dark:text-red-400 mt-2" />}
            </button>
          </div>
          <DialogFooter>
            <Button
              data-testid="customer-delete-cancel"
              variant="ghost"
              onClick={() => setToRemove(null)}
              disabled={!!removing}
              className="text-zinc-300 hover:bg-zinc-900"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toReactivate} onOpenChange={(o) => { if (!o) { setToReactivate(null); setReactivatePassword(""); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-green-brand" /> Reativar cliente
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {toReactivate
                ? `Defina uma nova senha para ${toReactivate.name}. Ele receberá um e-mail com o link do app e a nova senha.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={reactivate} className="space-y-3" data-testid="reactivate-form">
            <div>
              <Label className="text-xs font-mono-caps text-zinc-400">Nova senha</Label>
              <div className="mt-1 relative">
                <KeyRound className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  data-testid="reactivate-password-input"
                  required
                  minLength={8}
                  value={reactivatePassword}
                  onChange={(e) => setReactivatePassword(e.target.value)}
                  className="pl-9 bg-zinc-900 border-zinc-800"
                  placeholder={PASSWORD_HINT}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={reactivating}
                onClick={() => { setToReactivate(null); setReactivatePassword(""); }}
                className="text-zinc-300 hover:bg-zinc-900"
              >
                Cancelar
              </Button>
              <Button
                data-testid="reactivate-submit-btn"
                type="submit"
                disabled={reactivating}
                className="bg-green-brand hover:bg-green-brandDark text-white font-bold uppercase"
              >
                {reactivating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reativar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <WelcomeResultDialog result={welcome} onClose={() => setWelcome(null)} shopName={user?.shop?.name} />
    </div>
  );
}
